/**
 * 智能同步管理器
 * 协调 CLI、Web UI 和 VSCode 之间的数据同步
 */

import { EventEmitter } from 'events';
import { Task } from '../types';
import { logger } from '../utils/logger';
import { webhookService } from './webhook-service';

export interface SyncStrategy {
  name: string;
  priority: number;
  condition: (context: SyncContext) => boolean;
  delay: number;
  batch: boolean;
}

export interface SyncContext {
  operation: string;
  source: 'cli' | 'web' | 'vscode';
  taskCount: number;
  isUserAction: boolean;
  networkStatus: 'online' | 'offline' | 'slow';
  cpuUsage?: number;
  memoryUsage?: number;
}

export interface SyncMetrics {
  totalSyncs: number;
  successfulSyncs: number;
  failedSyncs: number;
  averageSyncTime: number;
  lastSyncTime: number;
  syncStrategy: string;
}

export class SyncManager extends EventEmitter {
  private static instance: SyncManager;
  private strategies: SyncStrategy[] = [];
  private syncMetrics: SyncMetrics = {
    totalSyncs: 0,
    successfulSyncs: 0,
    failedSyncs: 0,
    averageSyncTime: 0,
    lastSyncTime: 0,
    syncStrategy: 'default'
  };
  private pendingOperations: Map<string, any> = new Map();
  private syncTimer: NodeJS.Timeout | null = null;

  private constructor() {
    super();
    this.initializeStrategies();
  }

  static getInstance(): SyncManager {
    if (!SyncManager.instance) {
      SyncManager.instance = new SyncManager();
    }
    return SyncManager.instance;
  }

  /**
   * 初始化同步策略
   */
  private initializeStrategies(): void {
    // 1. 即时同步策略 - 用户直接操作
    this.strategies.push({
      name: 'immediate-user-action',
      priority: 100,
      condition: (ctx) => ctx.isUserAction && ctx.taskCount === 1,
      delay: 0,
      batch: false
    });

    // 2. 快速同步策略 - 状态变更
    this.strategies.push({
      name: 'fast-status-change',
      priority: 90,
      condition: (ctx) => 
        ['task.status', 'task.priority'].includes(ctx.operation) &&
        ctx.networkStatus === 'online',
      delay: 1000,
      batch: false
    });

    // 3. 延迟同步策略 - 内容编辑
    this.strategies.push({
      name: 'delayed-content-edit',
      priority: 80,
      condition: (ctx) => 
        ['task.edit', 'task.update'].includes(ctx.operation) &&
        ctx.taskCount <= 5,
      delay: 3000,
      batch: false
    });

    // 4. 批量同步策略 - 多任务操作
    this.strategies.push({
      name: 'batch-multi-tasks',
      priority: 70,
      condition: (ctx) => ctx.taskCount > 5 || ctx.operation === 'task.bulk',
      delay: 5000,
      batch: true
    });

    // 5. 离线优先策略 - 网络不稳定
    this.strategies.push({
      name: 'offline-first',
      priority: 60,
      condition: (ctx) => ctx.networkStatus === 'offline' || ctx.networkStatus === 'slow',
      delay: 0,
      batch: true
    });

    // 6. 资源节约策略 - 高负载时
    this.strategies.push({
      name: 'resource-saving',
      priority: 50,
      condition: (ctx) => 
        (ctx.cpuUsage && ctx.cpuUsage > 80) || 
        (ctx.memoryUsage && ctx.memoryUsage > 80),
      delay: 10000,
      batch: true
    });

    // 按优先级排序
    this.strategies.sort((a, b) => b.priority - a.priority);
  }

  /**
   * 选择最佳同步策略
   */
  private selectStrategy(context: SyncContext): SyncStrategy {
    // 查找第一个满足条件的策略
    for (const strategy of this.strategies) {
      if (strategy.condition(context)) {
        logger.debug(`Selected sync strategy: ${strategy.name}`);
        return strategy;
      }
    }

    // 默认策略
    return {
      name: 'default',
      priority: 0,
      condition: () => true,
      delay: 2000,
      batch: false
    };
  }

  /**
   * 智能同步任务变更
   */
  async syncTaskChange(
    operation: string,
    data: any,
    options: {
      source?: SyncContext['source'];
      isUserAction?: boolean;
    } = {}
  ): Promise<void> {
    const startTime = Date.now();

    try {
      // 构建同步上下文
      const context: SyncContext = {
        operation,
        source: options.source || 'cli',
        taskCount: Array.isArray(data) ? data.length : 1,
        isUserAction: options.isUserAction ?? true,
        networkStatus: await this.detectNetworkStatus(),
        cpuUsage: await this.getCPUUsage(),
        memoryUsage: await this.getMemoryUsage()
      };

      // 选择同步策略
      const strategy = this.selectStrategy(context);
      this.syncMetrics.syncStrategy = strategy.name;

      // 根据策略执行同步
      if (strategy.batch) {
        this.batchSync(operation, data, strategy.delay);
      } else if (strategy.delay > 0) {
        this.delayedSync(operation, data, strategy.delay);
      } else {
        await this.immediateSync(operation, data);
      }

      // 更新指标
      this.syncMetrics.totalSyncs++;
      this.syncMetrics.successfulSyncs++;
      this.syncMetrics.lastSyncTime = Date.now() - startTime;
      this.updateAverageSyncTime(this.syncMetrics.lastSyncTime);

      this.emit('sync-completed', {
        operation,
        strategy: strategy.name,
        duration: this.syncMetrics.lastSyncTime
      });

    } catch (error) {
      this.syncMetrics.totalSyncs++;
      this.syncMetrics.failedSyncs++;

      logger.error('Sync failed:', error);
      this.emit('sync-failed', { operation, error });
    }
  }

  /**
   * 即时同步
   */
  private async immediateSync(operation: string, data: any): Promise<void> {
    await webhookService.notify(operation, data);
  }

  /**
   * 延迟同步
   */
  private delayedSync(operation: string, data: any, delay: number): void {
    const key = `${operation}-${JSON.stringify(data)}`;
    
    // 取消之前的定时器
    const existing = this.pendingOperations.get(key);
    if (existing) {
      clearTimeout(existing);
    }

    // 设置新的定时器
    const timer = setTimeout(async () => {
      await this.immediateSync(operation, data);
      this.pendingOperations.delete(key);
    }, delay);

    this.pendingOperations.set(key, timer);
  }

  /**
   * 批量同步
   */
  private batchSync(operation: string, data: any, delay: number): void {
    // 清除现有定时器
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
    }

    // 收集批量操作
    const batch = this.pendingOperations.get('batch') || [];
    batch.push({ operation, data });
    this.pendingOperations.set('batch', batch);

    // 设置批量同步定时器
    this.syncTimer = setTimeout(async () => {
      const operations = this.pendingOperations.get('batch') || [];
      if (operations.length > 0) {
        await webhookService.notify('batch.sync', { operations });
        this.pendingOperations.delete('batch');
      }
      this.syncTimer = null;
    }, delay);
  }

  /**
   * 检测网络状态
   */
  private async detectNetworkStatus(): Promise<SyncContext['networkStatus']> {
    try {
      const start = Date.now();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);
      
      const response = await fetch('http://localhost:3001/health', {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      const duration = Date.now() - start;
      
      if (!response.ok) {
        return 'offline';
      }
      
      // 根据响应时间判断网络速度
      if (duration > 1000) {
        return 'slow';
      }
      
      return 'online';
    } catch {
      return 'offline';
    }
  }

  /**
   * 获取 CPU 使用率（简化版）
   */
  private async getCPUUsage(): Promise<number> {
    // 在实际应用中，这里应该使用 os.cpus() 等方法
    // 现在返回模拟值
    return Math.random() * 100;
  }

  /**
   * 获取内存使用率
   */
  private async getMemoryUsage(): Promise<number> {
    const used = process.memoryUsage();
    const total = require('os').totalmem();
    return (used.heapTotal / total) * 100;
  }

  /**
   * 更新平均同步时间
   */
  private updateAverageSyncTime(newTime: number): void {
    const { totalSyncs, averageSyncTime } = this.syncMetrics;
    this.syncMetrics.averageSyncTime = 
      (averageSyncTime * (totalSyncs - 1) + newTime) / totalSyncs;
  }

  /**
   * 获取同步指标
   */
  getMetrics(): SyncMetrics {
    return { ...this.syncMetrics };
  }

  /**
   * 重置指标
   */
  resetMetrics(): void {
    this.syncMetrics = {
      totalSyncs: 0,
      successfulSyncs: 0,
      failedSyncs: 0,
      averageSyncTime: 0,
      lastSyncTime: 0,
      syncStrategy: 'default'
    };
  }

  /**
   * 手动触发同步
   */
  async forceSyncAll(): Promise<void> {
    logger.info('Force syncing all pending operations');
    
    // 立即执行所有待处理的操作
    for (const [key, value] of this.pendingOperations) {
      if (key === 'batch') {
        const operations = value as any[];
        if (operations.length > 0) {
          await webhookService.notify('batch.sync', { operations });
        }
      } else if (typeof value === 'object' && value.timeout) {
        clearTimeout(value);
      }
    }
    
    this.pendingOperations.clear();
    
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
  }

  /**
   * 清理资源
   */
  cleanup(): void {
    // 清理所有定时器
    for (const [key, value] of this.pendingOperations) {
      if (typeof value === 'object' && value.timeout) {
        clearTimeout(value);
      }
    }
    
    this.pendingOperations.clear();
    
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
    
    this.removeAllListeners();
  }
}

// 导出单例
export const syncManager = SyncManager.getInstance();